import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConfirmActionModalProps } from "../ConfirmActionModal/types";

type BulkApprovalTarget = "elementTypes" | "cableGroups" | "quantityGroups";

interface UseBulkApprovalModalParams {
  planElementTypeIds: number[];
  planRouteTypeIds: number[];
  selectedDiagramItemTypeIds: number[];
  allPlanElementsApproved: boolean;
  allPlanCablesApproved: boolean;
  allDiagramItemsApproved: boolean;
  bulkApproveElementTypes: (ids: number[], approved: boolean) => void;
  bulkApproveRouteTypes: (ids: number[], approved: boolean) => void;
  bulkApproveQuantityTypes: (ids: number[], approved: boolean) => void;
}

interface UseBulkApprovalModalReturn {
  modalProps: ConfirmActionModalProps;
  openForElementTypes: () => void;
  openForCableGroups: () => void;
  openForQuantityGroups: () => void;
}

export const useBulkApprovalModal = ({
  planElementTypeIds,
  planRouteTypeIds,
  selectedDiagramItemTypeIds,
  allPlanElementsApproved,
  allPlanCablesApproved,
  allDiagramItemsApproved,
  bulkApproveElementTypes,
  bulkApproveRouteTypes,
  bulkApproveQuantityTypes,
}: UseBulkApprovalModalParams): UseBulkApprovalModalReturn => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<BulkApprovalTarget | null>(null);

  const close = () => setOpen(null);

  const modalProps = useMemo<ConfirmActionModalProps>(() => {
    if (open === "elementTypes") {
      const approve = !allPlanElementsApproved;
      return {
        isOpen: true,
        onClose: close,
        onConfirm: () => bulkApproveElementTypes(planElementTypeIds, approve),
        action: approve ? "approve" : "unapprove",
        title: t(
          approve
            ? "smartSurveyor.bulkApproval.approveAllTitle"
            : "smartSurveyor.bulkApproval.unapproveAllTitle",
        ),
        description: t(
          approve
            ? "smartSurveyor.bulkApproval.approveAllDescription"
            : "smartSurveyor.bulkApproval.unapproveAllDescription",
          { count: planElementTypeIds.length },
        ),
      };
    }

    if (open === "cableGroups") {
      const approve = !allPlanCablesApproved;
      return {
        isOpen: true,
        onClose: close,
        onConfirm: () => bulkApproveRouteTypes(planRouteTypeIds, approve),
        action: approve ? "approve" : "unapprove",
        title: t(
          approve
            ? "smartSurveyor.bulkApproval.approveCableGroupsTitle"
            : "smartSurveyor.bulkApproval.unapproveCableGroupsTitle",
        ),
        description: t(
          approve
            ? "smartSurveyor.bulkApproval.approveCableGroupsDescription"
            : "smartSurveyor.bulkApproval.unapproveCableGroupsDescription",
          { count: planRouteTypeIds.length },
        ),
      };
    }

    if (open === "quantityGroups") {
      const approve = !allDiagramItemsApproved;
      return {
        isOpen: true,
        onClose: close,
        onConfirm: () => bulkApproveQuantityTypes(selectedDiagramItemTypeIds, approve),
        action: approve ? "approve" : "unapprove",
        title: t(
          approve
            ? "smartSurveyor.bulkApproval.approveQuantityGroupsTitle"
            : "smartSurveyor.bulkApproval.unapproveQuantityGroupsTitle",
        ),
        description: t(
          approve
            ? "smartSurveyor.bulkApproval.approveQuantityGroupsDescription"
            : "smartSurveyor.bulkApproval.unapproveQuantityGroupsDescription",
          { count: selectedDiagramItemTypeIds.length },
        ),
      };
    }

    return {
      isOpen: false,
      onClose: close,
      onConfirm: () => {},
      action: "approve",
      title: "",
      description: "",
    };
  }, [
    open,
    allPlanElementsApproved,
    allPlanCablesApproved,
    allDiagramItemsApproved,
    planElementTypeIds,
    planRouteTypeIds,
    selectedDiagramItemTypeIds,
    bulkApproveElementTypes,
    bulkApproveRouteTypes,
    bulkApproveQuantityTypes,
    t,
  ]);

  return {
    modalProps,
    openForElementTypes: () => setOpen("elementTypes"),
    openForCableGroups: () => setOpen("cableGroups"),
    openForQuantityGroups: () => setOpen("quantityGroups"),
  };
};
